import { getApiUrl } from "../api";
import { useEffect, useMemo, useRef, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};

const getStoredUserId = () => {
  const keys = ["userId", "userid", "UserId", "boardid"];
  for (const key of keys) {
    const value = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (value && Number.isFinite(Number(value))) return String(Number(value));
  }

  return "0";
};

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const mapCategory = (item, index, language) => {
  const title = stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title));
  return {
    id: item.id || `in-kind-${index}`, title,
    image: getCloudflareImageUrl(item.ImagePath || item.image || item.icon, "w=180,sharpen=1"),
    description: stripHtml(language === "ar" ? (item.description_ar || item.description_en || item.description) : (item.description_en || item.description)),
  };
};

const defaultPickupPosition = { lat: 25.2048, lng: 55.2708 };
const donorContactMessage = {
  en: "Our team will contact you within 3 working days to coordinate your donation collection.",
  ar: "سيتواصل معك فريقنا خلال 3 أيام عمل لتنسيق استلام تبرعك.",
};
const googleMapsApiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyChY1FNqqq4IM1LwgAoTNvEMhF5Zg4GSdY").trim();

export default function InKindDonation({ language = "en" }) {
  const interactions = useHomeInteractions();
  const isArabic = language === "ar";
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const alertRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [pickupAddress, setPickupAddress] = useState("Downtown Dubai, Dubai, United Arab Emirates");
  const [pickupLat, setPickupLat] = useState(defaultPickupPosition.lat);
  const [pickupLng, setPickupLng] = useState(defaultPickupPosition.lng);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [pickupDate, setPickupDate] = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [donationPhotos, setDonationPhotos] = useState([]);
  const [locationDetecting, setLocationDetecting] = useState(false);
  const [fullName, setFullName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [communicationPreference, setCommunicationPreference] = useState("Email");
  const [donorNotes, setDonorNotes] = useState("");
  const [mapStatus, setMapStatus] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionReference, setSubmissionReference] = useState("");

  const selectedCategories = useMemo(
    () => categories.filter((category) => selectedCategoryIds.some((id) => String(id) === String(category.id))),
    [categories, selectedCategoryIds]
  );
  const selectedCategoryTitles = selectedCategories.map((category) => category.title).join(", ");
  const donationItems = useMemo(() => selectedCategories.map((category) => ({
    categoryId: category.id,
    categoryName: category.title,
    itemType: category.title,
    description: "",
    estimatedQuantity: 1,
    condition: "To Be Inspected",
  })), [selectedCategories]);
  const availableDates = useMemo(
    () => [...new Set(availabilitySlots.map((slot) => slot.date))],
    [availabilitySlots]
  );
  const availableTimeSlots = useMemo(
    () => availabilitySlots.filter((slot) => slot.date === pickupDate),
    [availabilitySlots, pickupDate]
  );
  const selectedSlot = useMemo(
    () => availabilitySlots.find((slot) => String(slot.id) === String(selectedSlotId)) || null,
    [availabilitySlots, selectedSlotId]
  );

  const toggleCategory = (category) => {
    const key = String(category.id);
    setSelectedCategoryIds((current) => current.some((id) => String(id) === key)
      ? current.filter((id) => String(id) !== key)
      : [...current, category.id]);
    setMessage("");
  };

  useEffect(() => {
    document.title = isArabic ? "التبرعات العينية | مؤسسة تراحم الخيرية" : "In Kind Donation | Tarahum Charity Foundation";
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.body.classList.remove("arabic-layout-page");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [isArabic]);

  useEffect(() => {
    setCategories([]);
  }, [language]);

  useEffect(() => {
    if (step !== 2 || !mapRef.current) return undefined;

    let isMounted = true;
    const listeners = [];
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (isMounted) {
        setMapStatus("Google Maps rejected this API key. Check Maps JavaScript API, Places API, Geocoding API, billing, and HTTP referrer restrictions.");
      }
      previousAuthFailure?.();
    };

    const loadGoogleMaps = () => new Promise((resolve, reject) => {
      const hasMapsConstructors = () => Boolean(window.google?.maps?.Map && window.google?.maps?.Marker && window.google?.maps?.Geocoder);
      const resolveMaps = () => {
        if (hasMapsConstructors()) {
          resolve(window.google.maps);
        } else {
          reject(new Error("Google Maps loaded without map constructors."));
        }
      };

      if (hasMapsConstructors()) {
        resolve(window.google.maps);
        return;
      }

      if (!googleMapsApiKey) {
        reject(new Error("Google Maps API key is not configured."));
        return;
      }

      const callbackName = "__tarahumPickupMapReady";
      const previousCallback = window[callbackName];
      window[callbackName] = () => {
        previousCallback?.();
        resolveMaps();
      };

      const existingScript = document.querySelector('script[data-google-maps="pickup"]');
      if (existingScript) {
        if (existingScript.dataset.googleMapsKey && existingScript.dataset.googleMapsKey !== googleMapsApiKey) {
          existingScript.remove();
        } else {
          existingScript.addEventListener("load", resolveMaps, { once: true });
          existingScript.addEventListener("error", () => reject(new Error("Unable to load Google Maps.")), { once: true });
          return;
        }
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.dataset.googleMaps = "pickup";
      script.dataset.googleMapsKey = googleMapsApiKey;
      script.addEventListener("error", () => reject(new Error("Unable to load Google Maps.")), { once: true });
      document.head.appendChild(script);
    });

    const updatePin = (maps, location, address) => {
      const lat = location.lat();
      const lng = location.lng();
      setPickupLat(lat);
      setPickupLng(lng);

      if (address) {
        setPickupAddress(address);
        if (searchInputRef.current) searchInputRef.current.value = address;
        return;
      }

      geocoderRef.current?.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          setPickupAddress(results[0].formatted_address);
          if (searchInputRef.current) searchInputRef.current.value = results[0].formatted_address;
        }
      });
    };

    loadGoogleMaps()
      .then((maps) => {
        if (!isMounted || !mapRef.current) return;

        const position = { lat: defaultPickupPosition.lat, lng: defaultPickupPosition.lng };
        const map = new maps.Map(mapRef.current, {
          center: position,
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        const marker = new maps.Marker({
          position,
          map,
          draggable: true,
          title: "Pickup Location",
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;
        geocoderRef.current = new maps.Geocoder();
        setMapStatus("");

        if (navigator.geolocation) {
          setLocationDetecting(true);
          navigator.geolocation.getCurrentPosition(({ coords }) => {
            if (!isMounted) return;
            const detected = new maps.LatLng(coords.latitude, coords.longitude);
            marker.setPosition(detected);
            map.setCenter(detected);
            map.setZoom(15);
            updatePin(maps, detected);
            setLocationDetecting(false);
          }, () => {
            if (!isMounted) return;
            setMapStatus(isArabic ? "تعذر تحديد موقعك تلقائياً. يمكنك تعديل الموقع يدوياً." : "We could not detect your location. You can adjust it manually.");
            setLocationDetecting(false);
          }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
        }

        listeners.push(map.addListener("click", (event) => {
          marker.setPosition(event.latLng);
          map.panTo(event.latLng);
          updatePin(maps, event.latLng);
        }));

        listeners.push(marker.addListener("dragend", (event) => {
          map.panTo(event.latLng);
          updatePin(maps, event.latLng);
        }));

        if (searchInputRef.current && maps.places?.Autocomplete) {
          const autocomplete = new maps.places.Autocomplete(searchInputRef.current, {
            fields: ["formatted_address", "geometry", "name"],
          });
          autocomplete.bindTo("bounds", map);
          listeners.push(autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (!place.geometry?.location) return;

            const location = place.geometry.location;
            marker.setPosition(location);
            map.setCenter(location);
            map.setZoom(15);
            updatePin(maps, location, place.formatted_address || place.name || "");
          }));
        }
      })
      .catch((error) => {
        if (isMounted) setMapStatus(error.message || "Google Maps could not be loaded.");
      });

    return () => {
      isMounted = false;
      window.gm_authFailure = previousAuthFailure;
      listeners.forEach((listener) => listener?.remove?.());
      markerRef.current?.setMap?.(null);
      markerRef.current = null;
      mapInstanceRef.current = null;
      geocoderRef.current = null;
    };
  }, [step, isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadCategories = async () => {
      try {
        const response = await fetch(getApiUrl("/api/GetDonationCategoryList.ashx?typ=In-Kind&pageSize=50"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`In-kind category request failed with ${response.status}`);

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        const mapped = rows
          .map((item, index) => mapCategory(item, index, language))
          .filter((category) => category.title && !/\bbooks?\b|كتب|كتاب/i.test(category.title));
        if (mapped.length) setCategories(mapped);
      } catch (error) {
        if (error.name !== "AbortError") console.warn("Unable to load in-kind donation categories", error);
      }
    };

    loadCategories();
    return () => controller.abort();
  }, [language]);

  useEffect(() => {
    if (step !== 2) return undefined;

    const controller = new AbortController();
    const loadAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      try {
        const response = await fetch(getApiUrl("/api/InKindDonationAvailability.ashx"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) {
          throw new Error(result?.message || "Unable to load available appointment times.");
        }
        setAvailabilitySlots(Array.isArray(result.data) ? result.data : []);
      } catch (error) {
        if (error.name !== "AbortError") {
          setAvailabilitySlots([]);
          setAvailabilityError(error.message || "Unable to load available appointment times.");
        }
      } finally {
        if (!controller.signal.aborted) setAvailabilityLoading(false);
      }
    };

    loadAvailability();
    return () => controller.abort();
  }, [step]);

  const showValidationMessage = (text) => {
    setMessage(text);
    setTimeout(() => {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const goToStep = (nextStep) => {
    if (nextStep === 2 && !selectedCategoryIds.length) {
      showValidationMessage("Please select at least one donation category.");
      return;
    }

    if (nextStep === 3 && (!fullName.trim() || !contactNumber.trim() || !emailAddress.trim() || !pickupAddress.trim())) {
      showValidationMessage(isArabic ? "يرجى إدخال الاسم ورقم التواصل والبريد الإلكتروني وموقع الاستلام." : "Please complete your name, contact number, email address, and pickup location.");
      return;
    }

    if (nextStep === 3 && !selectedSlot) {
      showValidationMessage(isArabic ? "يرجى اختيار تاريخ ووقت متاحين للاستلام." : "Please select an available pickup date and time slot.");
      return;
    }

    setMessage("");
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFiles = (event) => setDonationPhotos(Array.from(event.target.files || []).slice(0, 5));

  const submitDonation = async () => {
    if (isSubmitting) return;

    if (!selectedCategoryIds.length) {
      showValidationMessage("Please select at least one donation category.");
      setStep(1);
      return;
    }

    if (!selectedSlot) {
      showValidationMessage(isArabic ? "يرجى اختيار تاريخ ووقت متاحين للاستلام." : "Please select an available pickup date and time slot.");
      setStep(2);
      return;
    }

    const formData = new FormData();
    formData.append("userid", getStoredUserId());
    formData.append("items", JSON.stringify(donationItems));
    formData.append("pickupAddress", pickupAddress.trim());
    formData.append("pickupLat", String(pickupLat));
    formData.append("pickupLng", String(pickupLng));
    formData.append("pickupDate", selectedSlot.date);
    formData.append("pickupTime", `${selectedSlot.start} - ${selectedSlot.end}`);
    formData.append("pickupMethod", "Foundation Pickup");
    formData.append("slotId", String(selectedSlot.id));
    formData.append("donorContactMessage", donorContactMessage.en);
    formData.append("fullName", fullName.trim());
     formData.append("contactNumber", contactNumber.trim());
    formData.append("emailAddress", emailAddress.trim());
    formData.append("communicationPreference", communicationPreference);
    formData.append("donorNotes", donorNotes.trim());
    donationPhotos.forEach((file) => formData.append("item_0_photos", file, file.name));

    try {
      setMessage("");
      setIsSubmitting(true);
      const token = getJwtToken();
      const headers = { Accept: "application/json" };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const workflowSubmitUrl = getApiUrl("/api/InKindDonationWorkflowSubmit.ashx");

      const response = await fetch(workflowSubmitUrl, {
        method: "POST",
        headers,
        credentials: "include",
        body: formData,
      });

      const result = await response.json().catch(() => null);
      if (response.status === 401) {
        throw new Error("Your login session has expired. Please login again before submitting.");
      }

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Unable to submit in-kind donation request.");
      }

      setSubmissionReference(result.reference || (result.reqid ? `IKD-${result.reqid}` : ""));
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      showValidationMessage(error.message || "Unable to submit in-kind donation request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const text = isArabic ? {
    heroTitle: "اصنع فرقًا من خلال تبرعك العيني", heroDescription: "قدّم طلب التبرع بسهولة، وسيقوم فريقنا بتنسيق الاستلام معك.",
    start: "ابدأ التبرع", track: "تتبع تبرعاتي", login: "تسجيل الدخول / إنشاء حساب",
    donationDetails: "تفاصيل التبرع", donationHelp: "يرجى إدخال تفاصيل المواد المراد التبرع بها.", selectCategory: "اختر فئة أو أكثر من فئات التبرع",
    upload: "صور اختيارية", donationItems: "مواد التبرع", uploadHelp: "يمكنك إضافة ما يصل إلى 5 صور عامة إن توفرت. سيظل الفحص الميداني مطلوباً.", uploadPrompt: "اختر صوراً عامة للتبرع (اختياري)",
    quantity: "الكمية التقديرية", quantityPlaceholder: "مثال: 5 قطع", description: "وصف مختصر", descriptionPlaceholder: "صف المواد", condition: "الحالة", selectCondition: "اختر الحالة", new: "جديد", good: "بحالة جيدة", used: "مستعمل",
    next: "الخطوة التالية", back: "رجوع", pickupDetails: "بيانات الاستلام", pickupHelp: "أضف موقع الاستلام وبيانات التواصل، ثم اختر موعداً متاحاً.", pickupLocation: "موقع الاستلام", pickupDescription: "سنحاول تحديد موقعك بعد موافقتك. يمكنك تعديل العنوان أو العلامة على الخريطة يدوياً.", pickupAddress: "عنوان الاستلام", searchMap: "ابحث عن الموقع على خرائط Google", pinLocation: "تحديد الموقع", pickupDate: "تاريخ الاستلام", timeSlot: "الفترة الزمنية", selectTime: "اختر الفترة الزمنية", fullName: "الاسم الكامل", contact: "رقم التواصل", email: "البريد الإلكتروني", selectDate: "اختر تاريخاً متاحاً", loadingSlots: "جارٍ تحميل المواعيد المتاحة...", noSlots: "لا توجد مواعيد متاحة حالياً. يرجى المحاولة لاحقاً.", remaining: "متبقي",
    review: "المراجعة والتأكيد", reviewHelp: "يرجى مراجعة تفاصيل التبرع والاستلام قبل الإرسال النهائي.", donationReview: "مراجعة التبرع", donationType: "نوع التبرع", notSelected: "لم يتم الاختيار", notAdded: "لم تتم الإضافة", mapPin: "موقع الخريطة", submit: "إرسال التبرع", submitting: "جارٍ الإرسال...",
    conditions: "الشروط", conditionsHelp: "الشروط الواجب مراعاتها عند التقديم", important: "ملاحظة مهمة:", required: "معلومات مطلوبة", submitted: "تم الإرسال", thankYou: "شكرًا لتبرعك", submittedDescription: donorContactMessage.ar, reference: "مرجع التبرع", status: "الحالة", successful: "تم الإرسال بنجاح", another: "إرسال تبرع آخر"
  } : {
    heroTitle: "Make a Difference with Your In-Kind Donation", heroDescription: "Submit a simple donation request and our team will coordinate collection with you.",
    start: "Start Donation", track: "Track My Donations", login: "Login / Create Account", donationDetails: "Donation Details", donationHelp: "Select the types of items you would like to donate.", selectCategory: "Select One or More Donation Categories", upload: "Optional Photos", donationItems: "Donation Items", uploadHelp: "You may add up to 5 general photos if available. A physical inspection will still be required.", uploadPrompt: "Choose general donation photos (optional)", quantity: "Quantity Estimate", quantityPlaceholder: "e.g., 5 items", description: "Short Description", descriptionPlaceholder: "Describe the items", condition: "Condition", selectCondition: "Select Condition", new: "New", good: "Good Condition", used: "Used", next: "Next Step", back: "Back", pickupDetails: "Collection Details", pickupHelp: "Add your pickup location and contact details, then choose an available appointment.", pickupLocation: "Pickup Location", pickupDescription: "With your permission, we will detect your location. You can adjust the address or map pin manually.", pickupAddress: "Pickup Address", searchMap: "Search location on Google Maps", pinLocation: "Pin Location", pickupDate: "Pickup Date", timeSlot: "Time Slot", selectTime: "Select an available time", fullName: "Full Name", contact: "Contact Number", email: "Email Address", selectDate: "Select an available date", loadingSlots: "Loading available appointments...", noSlots: "No appointments are currently available. Please try again later.", remaining: "remaining", review: "Review & Confirm", reviewHelp: "Please review your donation and pickup details before final submission.", donationReview: "Donation Review", donationType: "Donation Categories", notSelected: "Not selected", notAdded: "Not added", mapPin: "Map Pin", submit: "Submit Donation", submitting: "Submitting...", conditions: "Conditions", conditionsHelp: "Conditions that must be observed when applying", important: "Important Note:", required: "Required information missing", submitted: "Submitted", thankYou: "Thank You for Your Donation", submittedDescription: donorContactMessage.en, reference: "Donation Reference", status: "Status", successful: "Submitted Successfully", another: "Submit Another Donation"
  };
  const timelineItems = isArabic ? [
    ["تفاصيل التبرع", "أدخل معلومات المواد المراد التبرع بها"],
    ["بيانات الاستلام", "أضف الموقع وبيانات التواصل"],
    ["المراجعة والتأكيد", "راجع التفاصيل وأرسل طلب التبرع"],
  ] : [
    ["Donation Details", "Enter donation item information"],
    ["Collection Details", "Add location and contact details"],
    ["Review & Confirm", "Check details and submit donation"],
  ];

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="abt-re-new">
        <div className="abt-container">
          <div className="abt-hero">
            <div className="abt-hero-content">
              <h1>{text.heroTitle}</h1>
              <p>{text.heroDescription}</p>
              <div className="abt-hero-actions">
                <a href="#in-kind-form" className="abt-btn abt-btn-primary">
                  <i className="fa-solid fa-heart"></i>
                  {text.start}
                </a>
                <Link to="/in-kind-assistance" className="abt-btn abt-btn-outline">
                  <i className="fa-solid fa-circle-info"></i>
                  {text.track}
                </Link>
                <Link to="/login" className="abt-btn abt-btn-outline">
                  <i className="fa-solid fa-user"></i>
                  {text.login}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kid-d-wrp" id="in-kind-form">
        <div className="container">
          <div className="k-wraps">
            <div className="pickup-header">
              <div className="pickup-timeline">
                {timelineItems.map(([title], index) => (
                  <div className="timeline-fragment" key={title}>
                    <div className={`timeline-item ${step === index + 1 ? "active" : ""}`}>
                      <div className="timeline-number">{index + 1}</div>
                      <span>{title}</span>
                    </div>
                    {index < timelineItems.length - 1 ? <div className="timeline-line"></div> : null}
                  </div>
                ))}
              </div>
            </div>

            <div className="mobile-timeline">
              {timelineItems.map(([title, subtitle], index) => (
                <div className={`mobile-step ${step === index + 1 ? "active" : ""}`} key={title}>
                  <div className="mobile-circle">{index + 1}</div>
                  <div>
                    <h4>{title}</h4>
                    <p>{subtitle}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pickup-content">
              <div className="pickup-card">
                {message ? (
                  <div className="in-kind-alert" role="alert" ref={alertRef}>
                    <span className="in-kind-alert-icon">
                      <i className="fa-solid fa-circle-exclamation"></i>
                    </span>
                    <span>
                      <strong>{text.required}</strong>
                      <small>{message}</small>
                    </span>
                  </div>
                ) : null}

                {isSubmitted ? (
                  <div className="success-screen active">
                    <div className="success-icon">
                      <i className="fa-solid fa-check"></i>
                    </div>
                    <h2>{text.thankYou}</h2>
                    <p>{text.submittedDescription}</p>
                    <div className="success-info">
                      <div>
                        <span>{text.reference}</span>
                        <strong>{submissionReference || text.submitted}</strong>
                      </div>
                      <div>
                        <span>{isArabic ? "تنسيق الاستلام" : "Collection coordination"}</span>
                        <strong>{isArabic ? donorContactMessage.ar : donorContactMessage.en}</strong>
                      </div>
                      <div>
                        <span>{text.pickupLocation}</span>
                        <strong>{pickupAddress}</strong>
                      </div>
                      <div>
                        <span>{text.status}</span>
                        <strong>{text.successful}</strong>
                      </div>
                    </div>
                    <div className="step-actions" style={{ justifyContent: "center" }}>
                      <button className="step-btn next-btn" type="button" onClick={() => window.location.reload()}>
                        {text.another}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pickup-layout">
                    <div className="pickup-main">
                      {step === 1 ? (
                        <div className="step-content active">
                          <h1 className="main-title">{text.donationDetails}</h1>
                          <p className="subtitle">{text.donationHelp}</p>

                          <div className="kid-info">
                            <div className="h-d-wrp">
                              <div className="mb-30">
                                <h3>{text.selectCategory}</h3>
                                <div className="donation-options">
                                  {categories.map((category) => (
                                    <button
                                      type="button"
                                      className={`donation-option ${selectedCategoryIds.some((id) => String(id) === String(category.id)) ? "selected" : ""}`}
                                      key={category.id}
                                      aria-pressed={selectedCategoryIds.some((id) => String(id) === String(category.id))}
                                      onClick={() => toggleCategory(category)}
                                    >
                                      <div className="sdm-s">
                                        <img src={category.image} alt="" />
                                      </div>
                                      <p>{category.title}</p>
                                      <div className="checkmark">
                                        <i className="fa fa-check"></i>
                                      </div>
                                      <div className="description">{category.description}</div>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {selectedCategoryIds.length ? <div className="upload-section active"><div className="upload-card"><h3>{text.upload}</h3><p>{text.uploadHelp}</p><label className="drop-zone"><p>{text.uploadPrompt}</p><input type="file" multiple accept="image/*" onChange={handleFiles} /></label><div className="image-preview">{donationPhotos.map((file) => <img src={URL.createObjectURL(file)} alt={file.name} key={`${file.name}-${file.lastModified}`} />)}</div></div></div> : null}
                            </div>
                          </div>

                          <div className="step-actions">
                            <button className="step-btn next-btn" type="button" onClick={() => goToStep(2)}>
                              {text.next}
                              <i className="fa-solid fa-arrow-right"></i>
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {step === 2 ? (
                        <div className="step-content active">
                          <h1 className="main-title">{text.pickupDetails}</h1>
                          <p className="subtitle">{text.pickupHelp}</p>
                          <div className="pickup-section">
                            <div className="pickup-card">
                              <h3>{text.pickupLocation}</h3>
                              <p>{text.pickupDescription}</p>
                              <div className="form-row">
                                <div className="form-group">
                                  <label htmlFor="pickup-address">{text.pickupAddress}</label>
                                  <input id="pickup-address" type="text" value={pickupAddress} onChange={(event) => setPickupAddress(event.target.value)} />
                                </div>
                              </div>
                              <div className="pickup-map-picker">
                                <label className="pickup-map-search" htmlFor="pickup-map-search">
                                  <i className="fa-solid fa-magnifying-glass"></i>
                                  <input
                                    id="pickup-map-search"
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder={text.searchMap}
                                    defaultValue={pickupAddress}
                                    onBlur={(event) => {
                                      if (event.target.value.trim()) setPickupAddress(event.target.value.trim());
                                    }}
                                  />
                                </label>
                                <div className="pickup-map-canvas" ref={mapRef}>
                                  <div className="pickup-map-placeholder">
                                    <i className="fa-solid fa-location-dot"></i>
                                    <span>{mapStatus || "Loading Google Map..."}</span>
                                  </div>
                                </div>
                                <div className="pickup-map-meta">
                                  <span><i className="fa-solid fa-map-pin"></i> {text.pinLocation}</span>
                                  <strong>{pickupLat.toFixed(5)}, {pickupLng.toFixed(5)}</strong>
                                </div>
                                {locationDetecting || mapStatus ? <p className="in-kind-dropoff-notice">{locationDetecting ? (isArabic ? "جارٍ تحديد موقعك بعد الحصول على إذنك..." : "Detecting your location with your permission…") : mapStatus}</p> : null}
                              </div>
                              <div className="form-row two-col in-kind-appointment-fields">
                                <div className="form-group">
                                  <label htmlFor="pickup-date">{text.pickupDate}</label>
                                  <select id="pickup-date" value={pickupDate} disabled={availabilityLoading || !availableDates.length} onChange={(event) => { setPickupDate(event.target.value); setSelectedSlotId(""); setMessage(""); }}>
                                    <option value="">{availabilityLoading ? text.loadingSlots : text.selectDate}</option>
                                    {availableDates.map((date) => <option value={date} key={date}>{new Intl.DateTimeFormat(isArabic ? "ar-AE" : "en-AE", { weekday: "long", year: "numeric", month: "long", day: "numeric" }).format(new Date(`${date}T00:00:00`))}</option>)}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label htmlFor="pickup-time">{text.timeSlot}</label>
                                  <select id="pickup-time" value={selectedSlotId} disabled={!pickupDate || availabilityLoading || !availableTimeSlots.length} onChange={(event) => { setSelectedSlotId(event.target.value); setMessage(""); }}>
                                    <option value="">{text.selectTime}</option>
                                    {availableTimeSlots.map((slot) => <option value={slot.id} key={slot.id}>{slot.start} - {slot.end} ({slot.remaining} {text.remaining})</option>)}
                                  </select>
                                </div>
                              </div>
                              {availabilityError || (!availabilityLoading && !availabilitySlots.length) ? <p className="in-kind-slot-status" role="status">{availabilityError || text.noSlots}</p> : null}
                              <div className="in-kind-alert" role="status"><span className="in-kind-alert-icon"><i className="fa-solid fa-calendar-check"></i></span><span><strong>{isArabic ? "موعد الاستلام" : "Pickup appointment"}</strong><small>{isArabic ? "اختر أحد المواعيد المتاحة أعلاه. سيتواصل معك فريقنا إذا لزم إجراء أي تنسيق إضافي." : "Choose an available appointment above. Our team will contact you if any further coordination is needed."}</small></span></div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label htmlFor="full-name">{text.fullName}</label>
                                  <input id="full-name" type="text" placeholder="John Smith" value={fullName} onChange={(event) => setFullName(event.target.value)} />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="contact-number">{text.contact}</label>
                                  <input id="contact-number" type="tel" placeholder="+971 50 123 4567" value={contactNumber} onChange={(event) => setContactNumber(event.target.value)} />
                                </div>
                                 <div className="form-group">
                                  <label htmlFor="email-address">{text.email}</label>
                                 <input id="email-address" type="email" placeholder="smith@abc.com" value={emailAddress} onChange={(event) => setEmailAddress(event.target.value)} />
                                </div>
                              </div>
                              <div className="form-row two-col">
                                <div className="form-group"><label htmlFor="communication-preference">{isArabic ? "طريقة التواصل المفضلة" : "Communication Preference"}</label><select id="communication-preference" value={communicationPreference} onChange={(event) => setCommunicationPreference(event.target.value)}><option>Email</option><option>SMS</option><option>WhatsApp</option></select></div>
                                <div className="form-group"><label htmlFor="donor-notes">{isArabic ? "ملاحظات عامة" : "General Donor Notes"}</label><input id="donor-notes" value={donorNotes} onChange={(event) => setDonorNotes(event.target.value)} /></div>
                              </div>
                            </div>
                          </div>
                          <div className="step-actions">
                            <button className="step-btn back-btn" type="button" onClick={() => goToStep(1)}>
                              <i className="fa-solid fa-arrow-left"></i>
                              {text.back}
                            </button>
                            <button className="step-btn next-btn" type="button" onClick={() => goToStep(3)}>
                              {text.next}
                              <i className="fa-solid fa-arrow-right"></i>
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {step === 3 ? (
                        <div className="step-content active">
                          <h1 className="main-title">{text.review}</h1>
                          <p className="subtitle">{text.reviewHelp}</p>
                          <div className="summary-card">
                            <div className="summary-head">
                              <i className="fa-regular fa-clipboard"></i>
                              <span>{text.donationReview}</span>
                            </div>
                            <div className="summary-body">
                              {[
                                ["fa-solid fa-box-open", text.donationType, selectedCategoryTitles || text.notSelected],
                                ["fa-solid fa-location-dot", text.pickupLocation, pickupAddress || text.notAdded],
                                ["fa-solid fa-calendar-day", text.pickupDate, selectedSlot ? new Intl.DateTimeFormat(isArabic ? "ar-AE" : "en-AE", { year: "numeric", month: "long", day: "numeric" }).format(new Date(`${selectedSlot.date}T00:00:00`)) : text.notSelected],
                                ["fa-regular fa-clock", text.timeSlot, selectedSlot ? `${selectedSlot.start} - ${selectedSlot.end}` : text.notSelected],
                                ["fa-solid fa-map-pin", text.mapPin, `${pickupLat.toFixed(5)}, ${pickupLng.toFixed(5)}`],
                                ["fa-solid fa-phone", text.contact, contactNumber || text.notAdded],
                                ["fa-solid fa-message", isArabic ? "طريقة التواصل" : "Communication", communicationPreference],
                              ].map(([icon, label, value]) => (
                                <div className="summary-item" key={label}>
                                  <i className={icon}></i>
                                  <div>
                                    <span>{label}</span>
                                    <strong>{value}</strong>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="in-kind-review-lines">
                              {donationItems.map((item) => <article key={item.categoryId}><strong>{item.categoryName}</strong><span>{isArabic ? "سيتم تأكيد التفاصيل بعد الفحص الميداني" : "Details will be confirmed during physical inspection"}</span></article>)}
                            </div>
                          </div>
                          <div className="step-actions">
                            <button className="step-btn back-btn" type="button" onClick={() => goToStep(2)}>
                              <i className="fa-solid fa-arrow-left"></i>
                              {text.back}
                            </button>
                            <button className="step-btn submit-final-btn" type="button" disabled={isSubmitting} onClick={submitDonation}>
                              <i className="fa-regular fa-paper-plane"></i>
                              {isSubmitting ? text.submitting : text.submit}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="sidebar">
                      <aside className="kid-side-card">
                        <div className="kid-card-head">
                          <div className="kid-card-icon">
                            <i className="fa-solid fa-list-check"></i>
                          </div>
                          <div>
                            <h2>{text.conditions}</h2>
                            <p>{text.conditionsHelp}</p>
                          </div>
                        </div>
                        <div className="kid-side-content">
                          <div className="kid-side-box">
                            <h3><i className="fa-solid fa-circle-check"></i> {text.conditions}</h3>
                            <ul className="kid-list">
                              <li><i className="fa-solid fa-check"></i>{isArabic ? "يجب ألا يقل وزن الملابس عن 15 إلى 20 كجم." : "The clothes weight must be a minimum of 15 to 20 kg"}</li>
                              <li><i className="fa-solid fa-check"></i>{isArabic ? "يجب أن تكون المواد المتبرع بها بحالة جيدة بنسبة لا تقل عن 80٪." : "The donation items must be in good condition with a minimum of 80%"}</li>
                              <li><i className="fa-solid fa-check"></i>{isArabic ? "يتحقق مسؤول الزيارة الميدانية من المواد قبل إرسال السائق." : "The field visit officer checks the donation items before sending the driver."}</li>
                              <li><i className="fa-solid fa-check"></i>{isArabic ? "نقبل فقط المواد التي يعتمدها مسؤول الزيارة الميدانية." : "We accept only the donation items approved by the field visit officer"}</li>
                             </ul>
                          </div>
                          <div className="kid-side-box">
                            <h3><i className="fa-solid fa-triangle-exclamation"></i> {text.important}</h3>
                            <div className="kid-note">
                              {isArabic ? "المواد الناقصة أو التالفة بأي شكل غير مقبولة. يجب أن تكون حالة المواد 80٪ أو أكثر." : "Items that are incomplete or damaged in any way are not considered acceptable. Items must be 80% or above in condition."}
                            </div>
                          </div>
                        </div>
                      </aside>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
